import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useDashboardMode } from '@/store/mode';
import { ListingReview } from '@/components/grow/ListingReview';
import { ViewsTrend } from '@/components/dashboard/ViewsTrend';

interface AdTier { ratePct: number; label: string; blurb: string }
interface ActivePromo {
  id: string; kind: 'SPONSORED' | 'FLASH' | 'BOOST' | 'FEATURE'; listingId: string; title: string;
  priceGBP: number; adRatePct?: number | null; estimatedFeeGBP?: number | null;
  discountPct?: number | null; expiresAt: string;
}
interface GrowListing {
  id: string; title: string; priceGBP: number; views: number; saves: number;
  sponsored: boolean; flash: boolean;
}
interface Overview {
  needsMigration?: boolean;
  pro: {
    active: boolean; interval?: string | null; expiresAt?: string | null;
    features: string[]; prices: { monthly: number; annual: number };
    // Free & permanent at freeAtSales lifetime sales; £9.99/mo until then.
    earnedFree?: boolean;
    source?: 'earned' | 'paid' | 'none';
    salesCount?: number;
    freeAtSales?: number;
    salesToFree?: number;
    minSalesToSubscribe?: number;
    canSubscribe?: boolean;
  };
  walletGBP: number;
  ads: { minRatePct: number; maxRatePct: number; defaultRatePct: number; durationDays: number; tiers: AdTier[]; spentGBP: number };
  flash: { minDiscountPct: number; durationHours: number; maxPerSeller: number; liveCount: number };
  active: ActivePromo[];
  listings: GrowListing[];
}
interface Automation {
  pro: boolean;
  autoRelist: boolean;
  offerAutoAcceptPct?: number | null;
  offerAutoDeclinePct?: number | null;
  aiListingsUsed: number;
  aiListingsLimit: number;
  listingsUsed: number;
  listingsLimit: number | null; // null = no cap (free sellers)
}
interface Analytics {
  pro: boolean;
  viewsSeries?: { day: string; views: number }[];
  basic: { liveListings: number; soldListings: number; views: number; saves: number; saveRatePct: number; sellThroughPct: number };
  funnel?: { views: number; saves: number; orders: number; viewToSavePct: number; saveToSalePct: number };
  revenue?: { last30GBP: number; orders: number; avgOrderGBP: number };
  topPerformers?: { id: string; title: string; priceGBP: number; views: number; saves: number }[];
  priceGuidance?: { id: string; title: string; priceGBP: number; suggestedGBP: number; reason: string }[];
}

export default function Grow() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // Active view (Buying / Selling). Computed identically to DashboardLayout so
  // Grow agrees with the nav: seller tools show only in the Selling view.
  const storedMode = useDashboardMode((s) => s.mode);
  const storedUserId = useDashboardMode((s) => s.userId);
  const setMode = useDashboardMode((s) => s.setMode);
  const mode = (storedMode && storedUserId === user?.id) ? storedMode : 'SELLER';
  const [adFor, setAdFor] = useState<GrowListing | null>(null);
  const [adRate, setAdRate] = useState(7);
  const [flashFor, setFlashFor] = useState<GrowListing | null>(null);
  const [flashPrice, setFlashPrice] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkPct, setBulkPct] = useState('10');

  const { data } = useQuery<Overview>({
    queryKey: ['grow'],
    queryFn: async () => (await api.get('/promotions/overview')).data,
    retry: false,
  });
  const { data: stats } = useQuery<Analytics>({
    queryKey: ['grow-analytics'],
    queryFn: async () => (await api.get('/promotions/analytics')).data,
    retry: false,
  });
  const { data: auto } = useQuery<Automation>({
    queryKey: ['grow-automation'],
    queryFn: async () => (await api.get('/promotions/automation')).data,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['grow'] });
    qc.invalidateQueries({ queryKey: ['grow-analytics'] });
    qc.invalidateQueries({ queryKey: ['my-listings'] });
    qc.invalidateQueries({ queryKey: ['wallet'] });
  };

  const sponsor = useMutation({
    mutationFn: async (p: { listingId: string; ratePct: number }) => (await api.post('/promotions/sponsor', p)).data,
    onSuccess: (r: any) => {
      toast.success(`Advertising live at ${r.adRatePct}% - you only pay if it sells.`);
      setAdFor(null); refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not start that campaign.'),
  });
  const stopAd = useMutation({
    mutationFn: async (listingId: string) => (await api.post('/promotions/sponsor/stop', { listingId })).data,
    onSuccess: () => { toast.success('Campaign stopped.'); refresh(); },
  });
  const flash = useMutation({
    mutationFn: async (p: { listingId: string; salePriceGBP: number }) => (await api.post('/promotions/flash', p)).data,
    onSuccess: (r: any) => {
      toast.success(`Flash sale live - ${r.discountPct.toFixed(0)}% off${r.watchersNotified ? `, ${r.watchersNotified} watchers alerted` : ''}.`);
      setFlashFor(null); setFlashPrice(''); refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not start that flash sale.'),
  });
  const bulkPrice = useMutation({
    mutationFn: async () => (await api.post('/listings/bulk/price', { listingIds: [...picked], reducePct: Number(bulkPct) })).data,
    onSuccess: (r: any) => {
      toast.success(`${r.updated} listing${r.updated === 1 ? '' : 's'} repriced${r.watchersNotified ? `, ${r.watchersNotified} watchers alerted` : ''}.`);
      setPicked(new Set()); refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not reprice those listings.'),
  });
  const bulkRelist = useMutation({
    mutationFn: async () => (await api.post('/listings/bulk/relist', { listingIds: [...picked] })).data,
    onSuccess: (r: any) => { toast.success(`${r.relisted} listing${r.relisted === 1 ? '' : 's'} back at the top.`); setPicked(new Set()); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not relist those.'),
  });
  const subscribePro = useMutation({
    mutationFn: async (p: 'monthly' | 'annual') => (await api.post('/promotions/pro/subscribe', { plan: p })).data,
    onSuccess: (r: any) => {
      if (r?.url) { window.location.href = r.url; return; } // hosted Stripe Checkout
      toast.success(r?.message ?? 'Souqli Pro is active 🎉');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not start Souqli Pro.'),
  });
  const cancelPro = useMutation({
    mutationFn: async () => (await api.post('/promotions/pro/cancel')).data,
    onSuccess: () => { toast.success('Souqli Pro will not renew.'); refresh(); },
  });
  const saveAutomation = useMutation({
    mutationFn: async (p: Partial<Automation>) => (await api.post('/promotions/automation', p)).data,
    onSuccess: () => { toast.success('Automation updated.'); qc.invalidateQueries({ queryKey: ['grow-automation'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save that setting.'),
  });

  const pro = data?.pro;
  const listings = data?.listings ?? [];
  const active = data?.active ?? [];
  const b = stats?.basic;

  const toggle = (id: string) => setPicked((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  // Seller tools belong to seller accounts.
  if (user && user.role !== 'SELLER' && user.role !== 'ADMIN') {
    return (
      <div className="rounded-2xl border border-warmline bg-white p-8 text-center max-w-[520px] mx-auto mt-8">
        <div className="text-4xl mb-3">🏷️</div>
        <h1 className="display text-xl text-ink">Seller tools</h1>
        <p className="text-mute text-sm mt-2 leading-relaxed">
          Promotion, analytics and Souqli Pro are part of a seller account.
          Switch your account to selling in your profile to unlock them.
        </p>
        <a href="/dashboard/profile" className="btn btn-primary mt-6">Go to profile</a>
      </div>
    );
  }

  // Seller account, but currently in the Buying view - keep seller features out
  // of the buyer view (mirror of Membership, which is hidden in the Selling
  // view). Offer a one-tap switch back to Selling rather than a dead end.
  if (mode === 'BUYER') {
    return (
      <div className="rounded-2xl border border-warmline bg-white p-8 text-center max-w-[520px] mx-auto mt-8">
        <div className="text-4xl mb-3">🛍️</div>
        <h1 className="display text-xl text-ink">You're in the Buying view</h1>
        <p className="text-mute text-sm mt-2 leading-relaxed">
          Promotion, analytics and Souqli Pro are seller tools. Switch to the
          Selling view to open Grow your store.
        </p>
        <button
          type="button"
          onClick={() => user && setMode('SELLER', user.id)}
          className="btn btn-primary mt-6"
        >
          Switch to Selling
        </button>
      </div>
    );
  }

  // Nothing listed yet - a gentle nudge, but the rest of the page (analytics
  // included) still renders below rather than replacing the whole view.
  const nothingToPromote = !!data && listings.length === 0 && (b?.soldListings ?? 0) === 0;

  const isPro = !!data?.pro.active;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display text-2xl text-ink mb-1">Grow your store</h1>
          <p className="text-mute text-sm">
            {isPro
              ? 'Your full toolkit - analytics, automation and promotion.'
              : 'Everything here is free unless it says otherwise. You only pay for ads if the item sells.'}
          </p>
        </div>
        <div className="text-right">
          <div className="stamp text-mute text-[9px]">WALLET</div>
          <div className="display text-2xl text-ink leading-none mt-1">£{(data?.walletGBP ?? 0).toFixed(2)}</div>
        </div>
      </div>

      {nothingToPromote && (
        <div className="rounded-2xl border border-warmline bg-white p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-ink">List your first item to unlock the full toolkit</div>
            <p className="text-[12px] text-mute mt-0.5">Advertise (pay only if it sells), run flash sales, and watch these numbers climb as buyers respond.</p>
          </div>
          <a href="/dashboard/sell" className="btn btn-primary btn-sm shrink-0">List an item</a>
        </div>
      )}

      {/* Two separate monthly allowances - how many listings you can create,
          and how many of them can use AI styling. Not the same number. */}
      {auto && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-luxe border border-warmline bg-white p-3.5">
            <div className="flex items-baseline justify-between">
              <div className="text-[13px] font-semibold text-ink">Listings this month</div>
              <div className="text-[13px] text-mute">{auto.listingsLimit == null ? `${auto.listingsUsed} · Unlimited` : `${auto.listingsUsed} / ${auto.listingsLimit}`}</div>
            </div>
            <div className="h-1.5 rounded-full bg-cream-deep overflow-hidden mt-2">
              <div
                className="h-full bg-ink rounded-full transition-all"
                style={{ width: auto.listingsLimit == null ? '100%' : `${Math.min(100, (auto.listingsUsed / auto.listingsLimit) * 100)}%` }}
              />
            </div>
          </div>
          <div className="rounded-luxe border border-warmline bg-white p-3.5">
            <div className="flex items-baseline justify-between">
              <div className="text-[13px] font-semibold text-ink">AI-styled listings this month</div>
              <div className="text-[13px] text-mute">{auto.aiListingsUsed} / {auto.aiListingsLimit}</div>
            </div>
            <div className="h-1.5 rounded-full bg-cream-deep overflow-hidden mt-2">
              <div className="h-full bg-gold rounded-full transition-all" style={{ width: `${Math.min(100, (auto.aiListingsUsed / auto.aiListingsLimit) * 100)}%` }} />
            </div>
            {!isPro && auto.aiListingsUsed >= auto.aiListingsLimit && (
              <p className="text-[11px] text-mute mt-1.5">Souqli Pro gives you more AI-styled listings a month.</p>
            )}
          </div>
        </div>
      )}

      {/* Free tools, stated as free. A seller who believes the page is a
          paywall never scrolls far enough to find the paid part worth having. */}
      {!isPro && (
        <div className="grid sm:grid-cols-4 gap-3">
          {[
            { i: '⚡', t: 'Flash sales',    s: 'Free. Alerts everyone watching.' },
            { i: '📣', t: 'Advertising',   s: 'Pay only when it sells.' },
            { i: '🔍', t: 'Listing review',s: 'Free. Why an item isn’t selling.' },
            { i: '📊', t: 'Basic stats',   s: 'Free. Views, saves, sell-through.' },
          ].map((x) => (
            <div key={x.t} className="rounded-luxe border border-warmline bg-white p-3.5">
              <div className="text-lg leading-none">{x.i}</div>
              <div className="text-[13px] font-semibold text-ink mt-2">{x.t}</div>
              <p className="text-[11px] text-mute mt-0.5 leading-snug">{x.s}</p>
            </div>
          ))}
        </div>
      )}

      {/* The review sits high on the page because it is the one thing here
          that answers the question sellers actually arrive with. */}
      <ListingReview listings={listings.map((l) => ({ id: l.id, title: l.title, views: l.views, saves: l.saves }))} />

      {data?.needsMigration && (
        <div className="rounded-luxe border border-warn/40 bg-warn/10 px-5 py-4">
          <div className="text-[13px] font-semibold text-ink">Promotion tools need a database update</div>
          <p className="text-[12px] text-ink-700 mt-1.5 leading-relaxed">
            Your wallet and listings are fine - advertising, flash sales and Souqli Pro just need the new
            tables. In the backend run:
          </p>
          <code className="mono block text-[12px] bg-ink text-cream rounded px-3 py-2 mt-2">
            npx prisma migrate deploy &amp;&amp; npx prisma generate
          </code>
          <p className="text-[11px] text-mute mt-1.5">Then restart the API and reload this page.</p>
        </div>
      )}

      {/* ── Performance analytics, free for everyone ── */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="display text-xl text-ink">Performance</h2>
          <span className="mono text-[10px] tracking-[0.2em] uppercase text-mute">Last 30 days</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: 'Live listings', v: (b?.liveListings ?? 0).toLocaleString(), c: `${b?.soldListings ?? 0} sold` },
            { k: 'Views',         v: (b?.views ?? 0).toLocaleString(),        c: 'real views, last 30 days' },
            { k: 'Save rate',     v: `${b?.saveRatePct ?? 0}%`,               c: 'of viewers hearted it' },
            { k: 'Sell-through',  v: `${b?.sellThroughPct ?? 0}%`,            c: 'listed → sold' },
          ].map((s) => (
            <div key={s.k} className="rounded-luxe border border-warmline bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-luxe">
              <div className="mono text-[9px] tracking-[0.18em] uppercase text-mute">{s.k}</div>
              <div className="display text-3xl text-ink mt-2 leading-none">{s.v}</div>
              <div className="text-[11px] text-mute mt-2">{s.c}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Analytics - free for every seller ── */}
      {stats && (
        <div className="rounded-luxe border border-warmline bg-white p-5 space-y-6">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-ink text-sm">Analytics · last 30 days</h2>
          </div>

          {/* Daily views trend */}
          {stats.viewsSeries && stats.viewsSeries.length > 0 && <ViewsTrend data={stats.viewsSeries} />}

          {/* Revenue row */}
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { k: 'Revenue',   v: `£${(stats.revenue?.last30GBP ?? 0).toFixed(2)}` },
              { k: 'Orders',    v: (stats.revenue?.orders ?? 0).toLocaleString() },
              { k: 'Avg order', v: `£${(stats.revenue?.avgOrderGBP ?? 0).toFixed(2)}` },
            ].map((s) => (
              <div key={s.k}>
                <div className="mono text-[9px] tracking-[0.18em] uppercase text-mute">{s.k}</div>
                <div className="display text-2xl text-ink mt-1 leading-none">{s.v}</div>
              </div>
            ))}
          </div>

          {/* Conversion funnel - Views → Saves → Sales, bars scaled to views */}
          {(() => {
            const fv = stats.funnel?.views ?? 0, fs = stats.funnel?.saves ?? 0, fo = stats.funnel?.orders ?? 0;
            const max = Math.max(fv, 1);
            const stages = [
              { label: 'Views', n: fv, pct: 100,             bar: 'bg-gold', conv: null as string | null },
              { label: 'Saves', n: fs, pct: (fs / max) * 100, bar: 'bg-sage', conv: `${stats.funnel?.viewToSavePct ?? 0}% of views` },
              { label: 'Sales', n: fo, pct: (fo / max) * 100, bar: 'bg-ok',   conv: `${stats.funnel?.saveToSalePct ?? 0}% of saves` },
            ];
            return (
              <div className="space-y-3">
                {stages.map((st) => (
                  <div key={st.label}>
                    <div className="flex items-baseline justify-between text-[12px]">
                      <span className="font-medium text-ink">{st.label}</span>
                      <span className="text-mute">{st.n.toLocaleString()}{st.conv && <span className="ml-2 text-[11px] text-mute-soft">· {st.conv}</span>}</span>
                    </div>
                    <div className="mt-1 h-2.5 rounded-full bg-cream-deep overflow-hidden">
                      <div className={`h-full rounded-full ${st.bar}`} style={{ width: `${st.n > 0 ? Math.max(st.pct, 4) : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Top performers - ranked by interest (views + saves×3) */}
          {!!stats.topPerformers?.length && (
            <div className="pt-4 border-t border-warmline">
              <div className="mono text-[9px] tracking-[0.18em] uppercase text-gold-dark mb-3">Top performers</div>
              <div className="space-y-2.5">
                {(() => {
                  const items = stats.topPerformers!.map((p) => ({ ...p, interest: p.views + p.saves * 3 }));
                  const max = Math.max(...items.map((p) => p.interest), 1);
                  return items.map((p) => (
                    <div key={p.id} className="grid grid-cols-[1.3fr_2fr_auto] gap-3 items-center">
                      <span className="truncate text-[13px] text-ink">{p.title}</span>
                      <div className="h-2 rounded-full bg-cream-deep overflow-hidden">
                        <div className="h-full rounded-full bg-gold" style={{ width: `${Math.max((p.interest / max) * 100, 4)}%` }} />
                      </div>
                      <span className="mono text-[10.5px] text-mute whitespace-nowrap">{p.views.toLocaleString()} views · {p.saves} saves</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Pricing suggestions */}
          {!!stats.priceGuidance?.length && (
            <div className="pt-4 border-t border-warmline">
              <div className="mono text-[9px] tracking-[0.18em] uppercase text-gold-dark mb-2">Pricing suggestions</div>
              {stats.priceGuidance.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-2 py-1.5 text-[13px]">
                  <span className="flex-1 min-w-0 truncate text-ink">{p.title}</span>
                  <span className="text-mute text-[11px]">{p.reason}</span>
                  <span className="text-ink">£{p.priceGBP.toFixed(0)} → <span className="text-gold-dark font-semibold">£{p.suggestedGBP}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Souqli Pro - £9.99/mo growth tier; opens at 10 sales, free once you pass 100 */}
      {!pro?.active && (
        <div id="pro" className="relative overflow-hidden rounded-2xl border-2 border-gold bg-gradient-to-br from-gold/12 to-gold/4 p-6 scroll-mt-6">
          <span className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gold/20 blur-2xl pointer-events-none" />
          <div className="relative flex flex-wrap items-center justify-between gap-5">
            <div className="min-w-0">
              <span className="inline-block rounded-full bg-gold px-2.5 py-1 text-[9px] font-bold tracking-widest text-ink mb-2.5">SOUQLI PRO</span>
              <h2 className="display text-xl text-ink leading-tight">See what actually sells</h2>
              <ul className="text-[13px] text-mute mt-2 space-y-1">
                {(pro?.features ?? []).map((f) => <li key={f}>✓ {f}</li>)}
              </ul>
            </div>
            {pro?.canSubscribe ? (
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => subscribePro.mutate('monthly')} disabled={subscribePro.isPending} className="btn btn-gold">
                  £{(pro?.prices.monthly ?? 9.99).toFixed(2)} / month
                </button>
                <button onClick={() => subscribePro.mutate('annual')} disabled={subscribePro.isPending} className="btn btn-outline btn-sm">
                  or £{(pro?.prices.annual ?? 99).toFixed(0)} / year
                </button>
              </div>
            ) : (
              <div className="shrink-0 text-right">
                <span className="inline-block rounded-full border border-warmline px-3 py-1 text-[11px] text-mute">🔒 Unlocks at {pro?.minSalesToSubscribe ?? 10} sales</span>
              </div>
            )}
          </div>

          <div className="relative mt-5 border-t border-gold/25 pt-4">
            {pro?.canSubscribe ? (
              <>
                <div className="flex items-end justify-between">
                  <span className="font-medium text-ink text-[13px]">Free once you pass {pro?.freeAtSales ?? 100} sales</span>
                  <span className="mono text-[12px] text-mute">{pro?.salesCount ?? 0} / {pro?.freeAtSales ?? 100}</span>
                </div>
                <div className="mt-2 h-1.5 bg-cream-deep rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${Math.min(100, ((pro?.salesCount ?? 0) / (pro?.freeAtSales ?? 100)) * 100)}%` }} />
                </div>
                <p className="text-[11px] text-mute mt-2 leading-snug">
                  {(pro?.salesToFree ?? 100) > 0
                    ? `${pro?.salesToFree} more completed sales and Pro is yours free, for good. Until then it is £${(pro?.prices.monthly ?? 9.99).toFixed(2)}/month, cancel any time.`
                    : 'You qualify for free Pro - refresh in a moment.'}
                </p>
              </>
            ) : (
              <>
                <div className="flex items-end justify-between">
                  <span className="font-medium text-ink text-[13px]">Pro unlocks at {pro?.minSalesToSubscribe ?? 10} sales</span>
                  <span className="mono text-[12px] text-mute">{pro?.salesCount ?? 0} / {pro?.minSalesToSubscribe ?? 10}</span>
                </div>
                <div className="mt-2 h-1.5 bg-cream-deep rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${Math.min(100, ((pro?.salesCount ?? 0) / (pro?.minSalesToSubscribe ?? 10)) * 100)}%` }} />
                </div>
                <p className="text-[11px] text-mute mt-2 leading-snug">
                  {Math.max(0, (pro?.minSalesToSubscribe ?? 10) - (pro?.salesCount ?? 0))} more completed {((pro?.minSalesToSubscribe ?? 10) - (pro?.salesCount ?? 0)) === 1 ? 'sale' : 'sales'} and you can subscribe at £{(pro?.prices.monthly ?? 9.99).toFixed(2)}/month. Pro is free once you pass {pro?.freeAtSales ?? 100} sales.
                </p>
              </>
            )}
            <p className="text-[11px] text-mute mt-1">Instant payouts are earned separately, through a clean sales record, not part of this plan.</p>
          </div>
        </div>
      )}

      {pro?.active && (
        <>
          <div className="rounded-luxe border border-gold/30 bg-gold/8 px-5 py-3 flex flex-wrap items-center justify-between gap-3">
            {pro.earnedFree ? (
              <span className="text-[13px] text-ink">
                ★ <span className="font-semibold">Souqli Pro</span> · free - earned with {pro.freeAtSales ?? 100}+ sales. Yours for good.
              </span>
            ) : (
              <>
                <span className="text-[13px] text-ink">
                  ★ <span className="font-semibold">Souqli Pro</span> · {pro.interval === 'year' ? 'annual' : 'monthly'} ·
                  renews {pro.expiresAt ? new Date(pro.expiresAt).toLocaleDateString() : 'automatically'}
                </span>
                <button onClick={() => cancelPro.mutate()} className="text-[12px] text-mute hover:text-bad underline underline-offset-2">Cancel Pro</button>
              </>
            )}
          </div>

          {/* ── Automation ── */}
          <div className="rounded-luxe border border-warmline bg-white p-5">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-semibold text-ink text-sm">Automation</h2>
              <span className="rounded-full bg-gold px-2 py-0.5 text-[8px] font-bold tracking-widest text-ink">PRO</span>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 accent-gold"
                checked={auto?.autoRelist ?? false}
                onChange={(e) => saveAutomation.mutate({ autoRelist: e.target.checked })}
              />
              <span>
                <span className="block text-[13px] font-medium text-ink">Auto-relist stale listings</span>
                <span className="block text-[12px] text-mute mt-0.5">
                  Anything untouched for a week quietly resurfaces at the top of &ldquo;newest&rdquo;. No manual bumping.
                </span>
              </span>
            </label>

            <div className="mt-5 pt-4 border-t border-warmline">
              <div className="text-[13px] font-medium text-ink mb-1">Offer automation</div>
              <p className="text-[12px] text-mute mb-3">Set it once and stop babysitting your inbox.</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px]">
                <label className="flex items-center gap-2">
                  <span className="text-sage-dark">Auto-accept at or above</span>
                  <input
                    type="number" min={50} max={100}
                    defaultValue={auto?.offerAutoAcceptPct ?? ''}
                    onBlur={(e) => saveAutomation.mutate({ offerAutoAcceptPct: e.target.value ? Number(e.target.value) : null })}
                    placeholder="90"
                    className="w-16 border border-warmline rounded px-2 py-1 text-right"
                  />
                  <span className="text-mute">% of asking</span>
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-bad">Auto-decline below</span>
                  <input
                    type="number" min={1} max={95}
                    defaultValue={auto?.offerAutoDeclinePct ?? ''}
                    onBlur={(e) => saveAutomation.mutate({ offerAutoDeclinePct: e.target.value ? Number(e.target.value) : null })}
                    placeholder="60"
                    className="w-16 border border-warmline rounded px-2 py-1 text-right"
                  />
                  <span className="text-mute">% of asking</span>
                </label>
              </div>
              <p className="text-[11px] text-mute mt-2.5">
                Anything in between still comes to you. Leave blank to handle every offer yourself.
              </p>
            </div>
          </div>
        </>
      )}

      {/* ── Running campaigns ── */}
      {active.length > 0 && (
        <div className="rounded-luxe border border-warmline bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-ink text-sm">Running now</h2>
            <span className="text-[11px] text-mute">Ad spend to date · £{(data?.ads.spentGBP ?? 0).toFixed(2)}</span>
          </div>
          <div className="space-y-2">
            {active.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 py-2 border-b border-warmline/60 last:border-0">
                <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold tracking-widest ${p.kind === 'FLASH' ? 'bg-bad text-purewhite' : 'bg-gold text-ink'}`}>
                  {p.kind === 'FLASH' ? '⚡ FLASH' : 'SPONSORED'}
                </span>
                <span className="flex-1 min-w-0 truncate text-sm text-ink">{p.title}</span>
                {p.adRatePct != null && (
                  <span className="text-[12px] text-mute">{p.adRatePct}% · ~£{(p.estimatedFeeGBP ?? 0).toFixed(2)} if it sells</span>
                )}
                {p.discountPct != null && <span className="text-[12px] text-sage-dark font-medium">−{p.discountPct.toFixed(0)}%</span>}
                {p.kind === 'SPONSORED' && (
                  <button onClick={() => stopAd.mutate(p.listingId)} className="text-[11px] text-mute hover:text-bad underline">Stop</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Listings + bulk actions ── */}
      <div className="rounded-luxe border border-warmline bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-warmline flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-ink text-sm">Your live listings</h2>
          {picked.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] text-mute">{picked.size} selected</span>
              <span className="flex items-center gap-1 text-[12px]">
                cut
                <input value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} type="number" min={1} max={90}
                       className="w-14 border border-warmline rounded px-1.5 py-0.5 text-right" />%
              </span>
              <button onClick={() => bulkPrice.mutate()} disabled={bulkPrice.isPending} className="btn btn-primary btn-sm">
                {bulkPrice.isPending ? 'Applying…' : 'Reprice'}
              </button>
              <button onClick={() => bulkRelist.mutate()} disabled={bulkRelist.isPending} className="btn btn-outline btn-sm">
                {bulkRelist.isPending ? 'Relisting…' : 'Relist'}
              </button>
              <button onClick={() => setPicked(new Set())} className="text-[12px] text-mute hover:underline">Clear</button>
            </div>
          ) : (
            <span className="text-[11px] text-mute">Select listings for bulk pricing and relisting - free for all sellers</span>
          )}
        </div>
        {listings.length === 0 ? (
          <p className="px-5 py-8 text-sm text-mute text-center">No live listings yet - list an item and promote it here.</p>
        ) : (
          <div className="divide-y divide-warmline/60">
            {listings.map((l) => (
              <div key={l.id} className="px-5 py-3 flex flex-wrap items-center gap-3">
                <input type="checkbox" className="accent-gold" checked={picked.has(l.id)} onChange={() => toggle(l.id)} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink truncate">{l.title}</div>
                  <div className="text-[11px] text-mute mt-0.5">
                    £{l.priceGBP.toFixed(0)} · {l.views} views · {l.saves} saves
                    {l.sponsored && <span className="text-gold-dark font-medium"> · sponsored</span>}
                    {l.flash && <span className="text-bad font-medium"> · flash</span>}
                  </div>
                </div>
                <button onClick={() => { setAdFor(l); setAdRate(data?.ads.defaultRatePct ?? 7); }} className="btn btn-outline btn-sm">
                  {l.sponsored ? 'Edit ad' : 'Advertise'}
                </button>
                <button
                  onClick={() => { setFlashFor(l); setFlashPrice(String(Math.floor(l.priceGBP * 0.75))); }}
                  className="btn btn-sm bg-bad/10 text-bad border border-bad/30 hover:bg-bad/20"
                >⚡ Flash</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Advertise modal ── */}
      {adFor && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setAdFor(null)}>
          <div className="bg-white rounded-2xl shadow-frame w-full max-w-[440px] max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-gold-dark text-[9px]">ADVERTISE LISTING</div>
            <h3 className="display text-xl mt-1 leading-tight">{adFor.title}</h3>
            <div className="mt-3 rounded-luxe border border-sage/40 bg-sage/10 px-3 py-2 text-[12px] text-sage-dark">
              ✓ You are <span className="font-semibold">only charged if this item sells</span>. No sale, no fee.
            </div>

            <div className="mt-4">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="label mb-0">Ad rate</span>
                <span className="display text-2xl text-gold-dark leading-none">{adRate}%</span>
              </div>
              <input
                type="range"
                min={data?.ads.minRatePct ?? 3}
                max={data?.ads.maxRatePct ?? 15}
                step={0.5}
                value={adRate}
                onChange={(e) => setAdRate(Number(e.target.value))}
                className="w-full accent-gold"
              />
              <div className="flex justify-between text-[10px] text-mute mt-1">
                <span>{data?.ads.minRatePct ?? 3}% · gentle</span>
                <span>{data?.ads.maxRatePct ?? 15}% · aggressive</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3">
              {(data?.ads.tiers ?? []).map((t) => (
                <button key={t.ratePct} onClick={() => setAdRate(t.ratePct)}
                  className={`rounded-luxe border px-2 py-2 text-center transition ${adRate === t.ratePct ? 'border-gold bg-gold/8' : 'border-warmline hover:border-gold/50'}`}>
                  <div className="text-[12px] font-semibold text-ink">{t.label}</div>
                  <div className="text-[11px] text-gold-dark mt-0.5">{t.ratePct}%</div>
                </button>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between rounded-luxe bg-cream-deep/50 px-3 py-2.5 text-[13px]">
              <span className="text-mute">If it sells at £{adFor.priceGBP.toFixed(0)}, you pay</span>
              <span className="display text-lg text-ink">£{(adFor.priceGBP * adRate / 100).toFixed(2)}</span>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setAdFor(null)} className="btn btn-outline flex-1">Cancel</button>
              <button onClick={() => sponsor.mutate({ listingId: adFor.id, ratePct: adRate })} disabled={sponsor.isPending} className="btn btn-primary flex-[1.4]">
                {sponsor.isPending ? 'Starting…' : 'Start advertising'}
              </button>
            </div>
            <p className="text-[11px] text-mute text-center mt-2.5">Runs for {data?.ads.durationDays ?? 30} days · stop any time</p>
          </div>
        </div>
      )}

      {/* ── Flash sale modal ── */}
      {flashFor && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/60 backdrop-blur-sm" onClick={() => setFlashFor(null)}>
          <div className="bg-white rounded-2xl shadow-frame w-full max-w-[420px] max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="stamp text-bad text-[9px]">⚡ FLASH SALE</div>
            <h3 className="display text-xl mt-1 leading-tight">{flashFor.title}</h3>
            <p className="text-[12px] text-mute mt-1.5">
              Free to join. Goes into Lightning Deals for {data?.flash.durationHours ?? 24} hours and everyone
              watching it gets alerted - the discount must be at least {data?.flash.minDiscountPct ?? 20}%.
            </p>
            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1">
                <label className="label">Flash price</label>
                <div className="flex items-center border border-warmline rounded-luxe overflow-hidden focus-within:border-gold">
                  <span className="pl-3 pr-1 text-mute">£</span>
                  <input type="number" min={1} value={flashPrice} onChange={(e) => setFlashPrice(e.target.value)}
                         className="flex-1 min-w-0 py-2 pr-3 outline-none" />
                </div>
              </div>
              <div className="text-right pb-2">
                <div className="text-[11px] text-mute">was £{flashFor.priceGBP.toFixed(0)}</div>
                <div className="text-sm font-semibold text-bad">
                  {Number(flashPrice) > 0 && Number(flashPrice) < flashFor.priceGBP
                    ? `−${Math.round((1 - Number(flashPrice) / flashFor.priceGBP) * 100)}%` : '-'}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setFlashFor(null)} className="btn btn-outline flex-1">Cancel</button>
              <button onClick={() => flash.mutate({ listingId: flashFor.id, salePriceGBP: Number(flashPrice) })}
                      disabled={flash.isPending || !Number(flashPrice)} className="btn btn-primary flex-[1.4]">
                {flash.isPending ? 'Starting…' : 'Start flash sale'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
